import { useState } from "react"
import {
  useGetRewardsByAccountQuery,
  useGetAccountReferralsQuery,
  useSerializedAccountData,
  useRafReward,
} from "@apis/accounts/useAccounts"
import { AccountRewardsByType } from "./AccountRewardsByType"
import { Loader2, UserPlus, Copy, ExternalLink, Eye } from "lucide-react"
import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import { AddReferralForm } from "../Referrals/AddReferralForm"
import { ReferralDetail } from "../Referrals/ReferralDetail"
import { copyToClipboard } from "@utils/copyToClipboard"

export function AccountRewards({ account_id }: { account_id: string }) {
  const [showAddReferral, setShowAddReferral] = useState(false)
  const { openDrawer } = useDrawer()
  const accountDataQuery = useSerializedAccountData(account_id)
  const getRewardsByAccountQuery = useGetRewardsByAccountQuery(account_id)
  const accountReferralsQuery = useGetAccountReferralsQuery(account_id)
  const rafRewardQuery = useRafReward(account_id)

  if (getRewardsByAccountQuery.isPending || accountDataQuery.isPending)
    return (
      <div className="tw:flex tw:justify-center tw:py-10">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )

  if (getRewardsByAccountQuery.isError) {
    return (
      <div className="tw:text-red-500">
        {`There was an error loading rewards. Error: ${getRewardsByAccountQuery.error?.message}`}
      </div>
    )
  }

  const openReferralDetails = () => {
    if (rafRewardQuery.data) {
      openDrawer(
        <ReferralDetail account_id={account_id} />,
        "Refer a Friend",
      )
    }
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:px-1">
      {accountReferralsQuery.data?.referrer_guid &&
        accountReferralsQuery.data?.referral_landing_page_url && (
          <div className="tw:space-y-3 tw:rounded-md tw:border tw:border-gray-300 tw:p-4">
            <div className="tw:pb-3 tw:border-b tw:border-gray-200">
              <h3 className="tw:mb-0 tw:font-semibold">
                Refer a Friend
              </h3>
              {accountReferralsQuery.data?.tagline && (
                <p className="tw:mb-0 tw:text-sm tw:text-gray-600">
                  {accountReferralsQuery.data.tagline}
                </p>
              )}
            </div>

            <div className="tw:space-y-1 tw:text-sm">
              <div className="tw:flex tw:items-center tw:gap-2">
                <span className="tw:font-semibold">Referral ID:</span>
                <span>{accountReferralsQuery.data.referrer_guid}</span>
              </div>
              <div className="tw:flex tw:items-center tw:gap-2">
                <span className="tw:font-semibold">
                  Shareable Signup Landing Page:
                </span>
                {/* External link to web-enrollment domain — <a> is correct here */}
                <a
                  href={accountReferralsQuery.data.referral_landing_page_url}
                  target="_blank"
                  rel="noreferrer"
                  className="tw:text-blue-600 tw:underline tw:hover:text-blue-800"
                >
                  {accountReferralsQuery.data.referral_landing_page_url}
                  <ExternalLink className="tw:ml-1 tw:inline tw:h-3 tw:w-3" />
                </a>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    copyToClipboard(
                      accountReferralsQuery.data!.referral_landing_page_url,
                    )
                  }
                >
                  <Copy className="tw:h-4 tw:w-4" />
                </Button>
              </div>
              <div className="tw:flex tw:items-center tw:gap-2">
                <span className="tw:font-semibold">Friends Referred:</span>
                <span>
                  {accountReferralsQuery.data?.referrals?.total_referrals ?? 0}
                </span>
              </div>
            </div>

            <div className="tw:flex tw:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openReferralDetails}
                disabled={!rafRewardQuery.data}
              >
                <Eye className="tw:mr-1 tw:h-4 tw:w-4" />
                View Referral Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddReferral(!showAddReferral)}
              >
                <UserPlus className="tw:mr-1 tw:h-4 tw:w-4" />
                Add a Referral
              </Button>
            </div>
          </div>
        )}

      {showAddReferral && accountReferralsQuery.data?.referrer_guid && (
        <AddReferralForm
          referrer_guid={accountReferralsQuery.data.referrer_guid}
          account_id={account_id}
        />
      )}

      <div>
        <h4 className="tw:mb-0 tw:text-xl tw:font-semibold">
          Inspire Rewards Program -{" "}
          {accountDataQuery.data?.planRewardsData?.plan_name}
        </h4>
        <p>{accountDataQuery.data?.planRewardsData.plan_rewards_description}</p>
      </div>

      {!getRewardsByAccountQuery.data.account_rewards_by_unit.length ? (
        <p>
          No rewards activity yet. Rewards will start to accrue once the account
          becomes Active.
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-4">
          {getRewardsByAccountQuery.data.account_rewards_by_unit.map(
            (type, index) => {
              return (
                <AccountRewardsByType
                  key={`${type.type}-${index}`}
                  type={type}
                  account_id={account_id}
                />
              )
            },
          )}
        </div>
      )}
    </div>
  )
}
